import { useState } from 'react'
import { Ban, Eye, Link2, Link2Off, MapPin, Pencil, ShieldAlert } from 'lucide-react'
import { Drawer, DrawerField } from '@/shared/ui/Drawer'
import { Modal } from '@/shared/ui/Modal'
import { EmptyState, ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Panel } from '@/components/erp/primitives'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { UnitPath } from '@/features/geography/UnitPath'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { listUsers } from '@/features/users/api'
import { fmtDate, fmtRelative } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import { cn } from '@/lib/utils'
import type {
  ActivityResponse, AdoptionResponse, MobilizerContactResponse, TaskResponse,
  TerminationImpact,
} from '@/shared/api/types'
import {
  activityFeed, changeMobilizerStatus, getMobilizer, linkUser, listAdoptions, listTasks,
  releaseStation, revealContact, terminateMobilizer, terminationPreview, unlinkUser,
} from './api'
import {
  ActivityChip, AdoptionChip, PriorityChip, RoleBadge, SourceChip, StatusChip,
  STATUS_ORDER, TaskStatusChip, statusLabel,
} from './vocab'
import { AssignmentsPanel } from './AssignmentsPanel'
import { MobilizerFormDialog } from './MobilizerFormDialog'

/**
 * One person, everything about them.
 *
 * ## The contact reveal is a button, not a field
 *
 * The full phone number and national ID are behind `GET /{id}/contact`, which
 * writes exactly one audit row per call naming the caller and the subject. That
 * makes it an *action*, so it renders as one — and the revealed value is held in
 * component state only, never cached, never re-fetched on a re-render, and gone
 * the moment the drawer closes. Anything else would turn "audited" into a lie:
 * one click would produce twelve audit rows, or none.
 *
 * ## Termination shows its consequences first
 *
 * `GET /termination-preview` and `POST /terminate` are computed by the same
 * method on the server, so the dialog cannot promise one thing and the button do
 * another. The preview is fetched when the dialog opens and its numbers are what
 * the confirm button commits to — including the refusal, when somebody has a
 * team and no supervisor of their own to hand them to.
 */

type Tab = 'overview' | 'units' | 'stations' | 'activity' | 'tasks'

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'units', label: 'Units & team' },
  { id: 'stations', label: 'Stations' },
  { id: 'activity', label: 'Activity' },
  { id: 'tasks', label: 'Tasks' },
]

export function MobilizerDrawer({
  id, onClose, onChanged, onOpenOther,
}: {
  id: string | null
  onClose: () => void
  onChanged: () => void
  onOpenOther?: (id: string) => void
}) {
  const [tab, setTab] = useState<Tab>('overview')
  const [editing, setEditing] = useState(false)
  const [terminating, setTerminating] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canUpdate = useCan('mobilizer:update')
  const canAssign = useCan('mobilizer:assign')
  const canTerminate = useCan('mobilizer:terminate')
  const canPii = useCan('mobilizer:read-pii')
  // Hoisted, deliberately. Calling this inside the `tab === 'stations'` branch
  // would change the hook count between renders the moment the tab changes,
  // which React treats as a different component and refuses.
  const canAdopt = useCan('mobilizer:adopt')

  const detail = useLoad(
    () => (id ? getMobilizer(id) : Promise.resolve(null)), [id])

  const d = detail.data
  const p = d?.profile

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await detail.reload()
      onChanged()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Drawer
        open={id !== null}
        onClose={onClose}
        width="lg"
        title={p?.fullName ?? 'Mobilizer'}
        subtitle={p ? (
          <span className="flex flex-wrap items-center gap-2">
            <RoleBadge code={p.roleCode} />
            <StatusChip code={p.statusCode} />
            <span className="font-mono text-erp-xs text-muted-foreground">{p.maskedPhone}</span>
          </span>
        ) : undefined}
        right={p && (
          <div className="flex gap-1.5">
            {canUpdate && (
              <button type="button" className="erp-btn" onClick={() => setEditing(true)}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            {canTerminate && p.statusCode !== 'TERMINATED' && (
              <button type="button" className="erp-btn text-red-700 dark:text-red-400"
                      onClick={() => setTerminating(true)}>
                <Ban className="h-3.5 w-3.5" /> End engagement
              </button>
            )}
          </div>
        )}
      >
        {detail.loading && !d ? (
          <LoadingBlock label="Loading…" />
        ) : !d || !p ? (
          <ErrorStrip message={detail.error ?? 'That mobilizer could not be loaded.'} />
        ) : (
          <div className="space-y-3">
            <ErrorStrip message={error} />

            <nav className="flex gap-1 border-b border-border px-1" role="tablist">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    'px-3 py-2 text-erp-sm border-b-2 -mb-px whitespace-nowrap',
                    tab === t.id
                      ? 'border-chocolate text-chocolate font-medium'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </nav>

            {tab === 'overview' && (
              <div className="space-y-3">
                {p.statusCode === 'TERMINATED' && (
                  <Note tone="bad">
                    This engagement ended{d.terminationReason ? `: ${d.terminationReason}` : '.'}{' '}
                    Their assignments and their activity are still here — history is not
                    deleted with people.
                  </Note>
                )}

                <dl className="erp-card overflow-hidden">
                  <DrawerField label="Full name" value={p.fullName} />
                  <DrawerField label="Role" value={<RoleBadge code={p.roleCode} />} />
                  <DrawerField label="Status" value={<StatusChip code={p.statusCode} />} />
                  <DrawerField label="Home unit" value={
                    <UnitPath stationId={p.primaryUnitId} path={p.primaryUnitPath} />
                  } />
                  <DrawerField label="Joined" value={fmtDate(d.joinedOn)} />
                  <DrawerField label="Email" value={p.email ?? '—'} mono />
                  <DrawerField label="Phone" value={
                    <ContactReveal mobilizerId={p.id} masked={p.maskedPhone} allowed={canPii} />
                  } />
                  <DrawerField label="Login" value={
                    p.userId ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 text-erp-sm">
                          <Link2 className="h-3.5 w-3.5" /> Linked to a user account
                        </span>
                        {canUpdate && (
                          <button type="button" className="erp-btn" disabled={busy}
                                  onClick={() => void act(() => unlinkUser(p.id))}>
                            <Link2Off className="h-3.5 w-3.5" /> Unlink
                          </button>
                        )}
                      </span>
                    ) : (
                      <span className="space-y-2 block">
                        <span className="block text-muted-foreground text-erp-sm">
                          No login. Most mobilizers have none — a person and a user account
                          are separate records, joined only when somebody needs to sign in.
                        </span>
                        {canUpdate && (
                          <LinkUserControl
                            busy={busy}
                            onLink={(userId) => void act(() => linkUser(p.id, userId))}
                          />
                        )}
                      </span>
                    )
                  } />
                  <DrawerField label="Notes" value={d.notes ?? '—'} />
                </dl>

                {canUpdate && p.statusCode !== 'TERMINATED' && (
                  <Panel title="Change status"
                         description="Suspending stops them counting toward coverage. It does not release their stations — ending the engagement does that.">
                    <div className="flex flex-wrap gap-1.5">
                      {STATUS_ORDER.filter((sc) => sc !== 'TERMINATED' && sc !== p.statusCode).map((sc) => (
                        <button
                          key={sc}
                          type="button"
                          className="erp-btn"
                          disabled={busy}
                          onClick={() => void act(() => changeMobilizerStatus(p.id, sc))}
                        >
                          Mark {statusLabel(sc).toLowerCase()}
                        </button>
                      ))}
                    </div>
                  </Panel>
                )}
              </div>
            )}

            {tab === 'units' && (
              <AssignmentsPanel
                detail={d}
                canAssign={canAssign}
                onChanged={() => { void detail.reload(); onChanged() }}
                onOpenOther={onOpenOther}
              />
            )}

            {tab === 'stations' && (
              <StationsTab
                mobilizerId={p.id}
                canRelease={canAdopt}
                onChanged={() => { void detail.reload(); onChanged() }}
              />
            )}

            {tab === 'activity' && <ActivityTab mobilizerId={p.id} />}

            {tab === 'tasks' && <TasksTab mobilizerId={p.id} />}
          </div>
        )}
      </Drawer>

      {p && (
        <MobilizerFormDialog
          open={editing}
          existing={d}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); void detail.reload(); onChanged() }}
        />
      )}

      {p && (
        <TerminateDialog
          open={terminating}
          mobilizerId={p.id}
          fullName={p.fullName}
          onClose={() => setTerminating(false)}
          onDone={() => { setTerminating(false); void detail.reload(); onChanged() }}
        />
      )}
    </>
  )
}

/**
 * One click, one audit row.
 *
 * The value lives in state and nowhere else: not in the loader, not in a cache,
 * not in the parent. Re-rendering does not re-fetch it, and closing the drawer
 * unmounts it. That is the difference between an audit trail that means
 * something and one that records a render loop.
 */
function ContactReveal({
  mobilizerId, masked, allowed,
}: { mobilizerId: string; masked?: string | null; allowed: boolean }) {
  const [shown, setShown] = useState<MobilizerContactResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (shown) {
    return (
      <span className="space-y-1 block">
        <span className="block font-mono text-erp-sm">{shown.phone ?? '—'}</span>
        {shown.nationalId && (
          <span className="block font-mono text-erp-xs text-muted-foreground">
            ID {shown.nationalId}
          </span>
        )}
        <span className="block text-erp-xs text-muted-foreground">
          This reveal was recorded in the audit log against your account.
        </span>
      </span>
    )
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="font-mono text-erp-sm">{masked ?? '—'}</span>
      {allowed ? (
        <button
          type="button"
          className="erp-btn"
          disabled={busy}
          onClick={async () => {
            setBusy(true); setError(null)
            try { setShown(await revealContact(mobilizerId)) } catch (err) { setError(explain(err)) } finally { setBusy(false) }
          }}
        >
          <Eye className="h-3.5 w-3.5" /> {busy ? 'Revealing…' : 'Reveal (audited)'}
        </button>
      ) : (
        <span className="text-erp-xs text-muted-foreground">
          Revealing a number needs <code className="font-mono">mobilizer:read-pii</code>.
        </span>
      )}
      {error && <span className="text-erp-xs text-red-700 dark:text-red-400">{error}</span>}
    </span>
  )
}

/**
 * Linking an existing user account to this person.
 *
 * It exists because unlinking without a way back is a one-way door: a
 * coordinator who unlinks by mistake would have to go to the database. The
 * search is over users, not mobilizers — the point of the join is that they are
 * different records.
 */
function LinkUserControl({
  busy, onLink,
}: { busy: boolean; onLink: (userId: string) => void }) {
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([])
  return (
    <span className="flex flex-wrap items-end gap-2">
      <AsyncMultiSelect
        label="User account"
        selected={picked}
        onChange={(next) => setPicked(next.slice(-1))}
        placeholder="Search users by name or phone"
        search={async (term) => {
          const found = await listUsers({
            q: term, status: ['ACTIVE'], scopeLevel: [], clearance: [],
            roleId: '', neverLoggedIn: false, locked: false, sort: '',
          }, 0, 10)
          return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.phone }))
        }}
      />
      <button
        type="button"
        className="erp-btn"
        disabled={busy || picked.length !== 1}
        onClick={() => onLink(picked[0]!.value)}
      >
        <Link2 className="h-3.5 w-3.5" /> Link
      </button>
    </span>
  )
}

function StationsTab({
  mobilizerId, canRelease, onChanged,
}: { mobilizerId: string; canRelease: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useLoad(() => listAdoptions({ mobilizerId }), [mobilizerId])
  const rows: AdoptionResponse[] = load.data ?? []
  const active = rows.filter((r) => r.statusCode === 'ACTIVE')
  const past = rows.filter((r) => r.statusCode !== 'ACTIVE')

  const release = async (stationId: string) => {
    setBusy(stationId); setError(null)
    try {
      await releaseStation(mobilizerId, stationId, 'Released from the roster')
      await load.reload()
      onChanged()
    } catch (err) { setError(explain(err)) } finally { setBusy(null) }
  }

  return (
    <div className="space-y-3">
      <ErrorStrip message={error ?? load.error} />
      <Panel title={`Stations they are answerable for (${active.length})`}
             description="Adoption is not assignment. Assignment says where somebody works; adoption says which station they own, and exactly one person owns each.">
        {load.loading ? <LoadingBlock label="Loading…" />
          : active.length === 0 ? (
            <EmptyState title="No stations adopted"
                        hint="They may still be assigned to units and working there — adoption is the narrower claim." />
          ) : (
            <ul className="divide-y divide-border">
              {active.map((a) => (
                <li key={a.id} className="py-2 flex items-center gap-3">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-erp-sm font-medium truncate">{a.stationName ?? a.stationId}</span>
                    <span className="block text-erp-xs text-muted-foreground">
                      Since {fmtDate(a.adoptedAt)}
                    </span>
                  </span>
                  {canRelease && (
                    <button type="button" className="erp-btn" disabled={busy === a.stationId}
                            onClick={() => void release(a.stationId)}>
                      Release
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
      </Panel>

      {past.length > 0 && (
        <Panel title="Previously" description="Kept, because who covered a station in March is a question an election petition asks.">
          <ul className="divide-y divide-border">
            {past.map((a) => (
              <li key={a.id} className="py-2 flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-erp-sm truncate">{a.stationName ?? a.stationId}</span>
                  <span className="block text-erp-xs text-muted-foreground">
                    {fmtDate(a.adoptedAt)} → {fmtDate(a.releasedAt)}
                    {a.releaseReason ? ` · ${a.releaseReason}` : ''}
                  </span>
                </span>
                <AdoptionChip code={a.statusCode} />
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  )
}

function ActivityTab({ mobilizerId }: { mobilizerId: string }) {
  const load = useLoad(() => activityFeed({ mobilizerId: [mobilizerId] }, undefined, 30), [mobilizerId])
  const rows: ActivityResponse[] = load.data?.content ?? []
  return (
    <Panel title="What they have been doing"
           description="The append-only field log. Nothing here can be edited — an activity that can be edited is not evidence.">
      {load.loading ? <LoadingBlock label="Loading…" />
        : rows.length === 0 ? (
          <EmptyState title="Nothing recorded yet"
                      hint="Activity arrives when they capture something in the field, or when work they raised elsewhere is credited back to them." />
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((a) => (
              <li key={a.id} className="py-2 flex items-start gap-3">
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <ActivityChip code={a.activityTypeCode} />
                    <SourceChip code={a.sourceCode} />
                  </span>
                  <span className="block text-erp-xs text-muted-foreground mt-0.5">
                    {a.unitName ?? '—'}
                    {a.latitude !== null && a.latitude !== undefined
                      ? ` · located to ±${a.accuracyMetres ?? '?'} m` : ''}
                  </span>
                </span>
                <span className="text-erp-xs text-muted-foreground whitespace-nowrap"
                      title={a.occurredAt}>
                  {fmtRelative(a.occurredAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
    </Panel>
  )
}

function TasksTab({ mobilizerId }: { mobilizerId: string }) {
  const load = useLoad(() => listTasks({ mobilizerId }), [mobilizerId])
  const rows: TaskResponse[] = load.data ?? []
  return (
    <Panel title="Assigned work"
           description="Progress is recomputed from the activity log every night. Nobody types a number into it.">
      {load.loading ? <LoadingBlock label="Loading…" />
        : rows.length === 0 ? (
          <EmptyState title="No tasks assigned" hint="Missions create tasks; a mission has to be active before it can." />
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((t) => (
              <li key={t.id} className="py-2 flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-erp-sm truncate">{t.missionTitle ?? t.missionCode}</span>
                  <span className="flex flex-wrap items-center gap-1.5 mt-0.5">
                    <TaskStatusChip code={t.statusCode} />
                    <PriorityChip code={t.priorityCode} />
                    {t.dueOn && (
                      <span className="text-erp-xs text-muted-foreground">Due {fmtDate(t.dueOn)}</span>
                    )}
                  </span>
                </span>
                <span className="erp-metric text-erp-sm whitespace-nowrap">
                  {t.progressCount}{t.targetCount ? ` / ${t.targetCount}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
    </Panel>
  )
}

/**
 * The one destructive path, and the one nobody specifies.
 *
 * The preview is loaded when the dialog opens, and its numbers are exactly what
 * the button commits to — same server-side computation, so they cannot disagree.
 * `blocked` is the case that matters: somebody with a team and no supervisor of
 * their own cannot be ended, because their subordinates would be orphaned. The
 * fix is to give *them* a supervisor first, and the dialog says so instead of
 * refusing with a bare 409.
 */
function TerminateDialog({
  open, mobilizerId, fullName, onClose, onDone,
}: {
  open: boolean
  mobilizerId: string
  fullName: string
  onClose: () => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = useLoad(
    () => (open ? terminationPreview(mobilizerId) : Promise.resolve(null)),
    [open, mobilizerId])

  const impact: TerminationImpact | null = preview.data

  const run = async () => {
    setBusy(true); setError(null)
    try {
      await terminateMobilizer(mobilizerId, reason.trim())
      setReason('')
      onDone()
    } catch (err) { setError(explain(err)) } finally { setBusy(false) }
  }

  return (
    <Modal
      open={open}
      onClose={() => { setReason(''); setError(null); onClose() }}
      title={`End ${fullName}'s engagement`}
      description="This releases their stations, returns their open tasks and re-parents their team. Their assignments and their activity stay."
      footer={(
        <>
          <button type="button" className="erp-btn" onClick={() => { setReason(''); onClose() }}>
            Cancel
          </button>
          <button
            type="button"
            className="erp-btn-primary"
            disabled={busy || reason.trim().length < 5 || !impact || impact.blocked}
            onClick={() => void run()}
          >
            {busy ? 'Ending…' : 'End engagement'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error ?? preview.error} />
        {preview.loading && !impact ? (
          <LoadingBlock label="Working out what this would do…" />
        ) : impact ? (
          <>
            {impact.blocked && (
              <Note tone="bad">
                <span className="inline-flex items-start gap-2">
                  <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{impact.blockReason}</span>
                </span>
              </Note>
            )}
            <ul className="erp-card divide-y divide-border/60">
              <ImpactRow label="Stations released" value={impact.adoptionsReleased}
                         hint="Coverage changes the moment this is confirmed." />
              <ImpactRow label="Open tasks returned" value={impact.tasksReturned}
                         hint="They go back to the pool, not to nobody." />
              <ImpactRow
                label="Team members re-parented"
                value={impact.subordinatesReparented}
                hint={impact.newSupervisorName
                  ? `They will report to ${impact.newSupervisorName}.`
                  : 'Nobody to hand them to — that is why this is blocked.'}
              />
              <ImpactRow label="Assignments kept" value={impact.assignmentsKept}
                         hint="Rows stay. They stop counting toward coverage because the person ended, not because the rows went." />
            </ul>
            {impact.subordinates.length > 0 && (
              <p className="text-erp-xs text-muted-foreground">
                Moving: {impact.subordinates.map((s) => s.fullName).join(', ')}
              </p>
            )}
          </>
        ) : null}

        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">
            Reason (at least five characters — it is kept on the record)
          </span>
          <textarea
            className="erp-input w-full min-h-20"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Moved out of the county"
          />
        </label>
      </div>
    </Modal>
  )
}

function ImpactRow({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <li className="px-3 py-2 flex items-start gap-3">
      <span className="erp-metric text-erp-lg font-semibold w-10 text-right shrink-0">{value}</span>
      <span className="min-w-0">
        <span className="block text-erp-sm">{label}</span>
        <span className="block text-erp-xs text-muted-foreground">{hint}</span>
      </span>
    </li>
  )
}
